import { BatchQueue } from './batch-queue'
import {
    PlaygamaRumEvent,
    RawPlaygamaCTREvent,
    RawPlaygamaEvent,
    RawPlaygamaSurveyGameEvent,
    RawPlaygamaUIEvent,
    RawRumMetricData,
    RumMetricResult,
} from './types'

const BATCH_CONFIG = {
    flushIntervalMs: 5000,
    maxBatchSizeBytes: 25000,
    maxBatchSizeCount: 20,
}

export class Analytics {
    private portalEventsQueue: BatchQueue<RawPlaygamaEvent>
    private rumEventsQueue: BatchQueue<PlaygamaRumEvent>
    private uiEventsQueue: BatchQueue<RawPlaygamaUIEvent>
    private ctrEventsQueue: BatchQueue<RawPlaygamaCTREvent>
    private surveyEventsQueue: BatchQueue<RawPlaygamaSurveyGameEvent>
    private pendingUnloadEvents = new Map<symbol, RawPlaygamaEvent>()

    constructor(baseEndpoint: string) {
        const batchConfig = {
            enabled: process.env.NODE_ENV === 'production',
            ...BATCH_CONFIG,
        }

        this.portalEventsQueue = new BatchQueue<RawPlaygamaEvent>(`${baseEndpoint}/portal/events`, batchConfig)
        this.rumEventsQueue = new BatchQueue<PlaygamaRumEvent>(`${baseEndpoint}/rum/events`, batchConfig)
        this.uiEventsQueue = new BatchQueue<RawPlaygamaUIEvent>(`${baseEndpoint}/portal/ui/events`, batchConfig)
        this.ctrEventsQueue = new BatchQueue<RawPlaygamaCTREvent>(`${baseEndpoint}/portal/ctr/events`, batchConfig)
        this.surveyEventsQueue = new BatchQueue<RawPlaygamaSurveyGameEvent>(
            `${baseEndpoint}/portal/survey`,
            batchConfig,
        )

        this.setupPageUnloadFlush()
    }

    log(event: RawPlaygamaEvent) {
        this.portalEventsQueue.add(event)
    }

    logUiEvent(event: RawPlaygamaUIEvent) {
        this.uiEventsQueue.add(event)
    }

    logCTREvent(event: RawPlaygamaCTREvent) {
        this.ctrEventsQueue.add(event)
    }

    logSurveyEvent(event: RawPlaygamaSurveyGameEvent) {
        this.surveyEventsQueue.add(event)
    }

    private logRum(event: PlaygamaRumEvent) {
        this.rumEventsQueue.add(event)
    }

    startRumEvent(metric: RawRumMetricData) {
        const start = Date.now()
        let isSent = false

        return (status: RumMetricResult) => {
            if (isSent) {
                console.trace('Sending metric after its already sent')
                return
            }
            const now = Date.now()
            this.logRum({
                ...metric,
                ...status,
                durationMs: now - start,
                finishTimestamp: now,
            })
            isSent = true
        }
    }

    registerPageHideFallbackEvent(event: RawPlaygamaEvent): () => void {
        const key = Symbol()
        this.pendingUnloadEvents.set(key, event)
        return () => this.pendingUnloadEvents.delete(key)
    }

    private setupPageUnloadFlush() {
        const flushAll = () => {
            for (const event of this.pendingUnloadEvents.values()) {
                this.portalEventsQueue.add(event)
            }
            this.pendingUnloadEvents.clear()

            this.portalEventsQueue.flush()
            this.rumEventsQueue.flush()
            this.uiEventsQueue.flush()
            this.ctrEventsQueue.flush()
            this.surveyEventsQueue.flush()
        }

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                flushAll()
            }
        })

        window.addEventListener('pagehide', flushAll)
    }
}
